// @flow
import { BellOutlined } from '@ant-design/icons'
import { Avatar, Select } from 'antd'
import map from 'lodash/map'
import { useLoadUsersLabels } from 'queries/users/useLoadUsers'
import * as React from 'react'
import { formatPhoneNumberIntl } from 'react-phone-number-input'
import { Link } from 'react-router-dom'
import type { LabelAssignedResponse } from 'types/assigned_response'
import type { Customer } from 'types/customer'
import Flex from 'ui/atoms/Flex'
import { buildPhoneNumberValue } from 'ui/atoms/PhoneInput'
import Text from 'ui/atoms/Text'
import Tooltip from 'ui/atoms/Tooltip'
import { hasText, momentLocal } from 'utils/helpers'
import { ZentakeContext } from 'utils/ZentakeContext'

import FormResponseInfo from './FormResponseInfo'

type Props = {
  response: ?Response,
  packetResponse: ?PacketResponse,
  message: ?Message,
  messageType: ?string,
  deliveryMethod: ?string,
  email: ?string,
  phone: ?string,
  createdAt: ?string,
  dateCompleted: ?string,
  customer: ?Customer,
  internalUser: ?string,
  reminders: ?({
    created_at: string,
    status: string,
  }[]),
  assignedResponses: ?Array<LabelAssignedResponse>,
  setAssignedResponses: (users: number[]) => Promise<void>,
  assignedLoading: boolean,
}

function FormResponseInfoList({
  response,
  packetResponse,
  message,
  setAssignedResponses,
  assignedLoading,
}: Props): React.Node {
  const { teamDetails } = React.useContext(ZentakeContext)
  const { data: users, isLoading: isUsersLoading } = useLoadUsersLabels()
  const [infoList, setInfoList] = React.useState([])

  const customer = response?.customer || packetResponse?.customer
  const messageType = message?.message_type
  const deliveryMethod = message?.preferred_delivery_method
  const email = message?.target_email
  const phone = message?.target_phone
  const createdAt = response?.created_at || packetResponse?.created_at
  const dateCompleted = response?.submitted_at || packetResponse?.submitted_at
  const reminders = response?.reminders
  const clientId = customer?.id
  const firstName = customer?.first_name
  const lastName = customer?.last_name
  const internalUser = response?.internal_user
  const assignedResponses =
    response?.assigned_responses || packetResponse?.assigned_responses

  const isPartOfPacket = response?.packet_response

  const remindersDate = React.useMemo(
    () =>
      reminders?.map(({ created_at }) =>
        momentLocal(created_at).format('MM-DD-YYYY')
      ),
    [reminders]
  )

  const assignedUserOptions = React.useMemo(() => {
    return (
      users?.map(({ id, first_name, last_name }) => ({
        label:
          first_name || last_name
            ? `${first_name}${first_name && last_name && ' '}${last_name}`
            : 'Unknown',
        value: id,
      })) || []
    )
  }, [users])

  const getUserLabel = React.useCallback(
    (userId: string) => {
      const user = users?.find(({ id }) => id === userId)
      if (!user) return ''
      const [first, last] = [user.first_name, user.last_name]
      if (first && last) {
        return `${first ? first[0].toUpperCase() : ''}${
          last ? last[0].toUpperCase() : ''
        }`
      }
      if (first) {
        return `${first[0].toUpperCase()}${first[1].toUpperCase()}`
      }
      if (last) {
        return `${last[0].toUpperCase()}${last[1].toUpperCase()}`
      }
      return 'UN'
    },
    [users]
  )

  React.useEffect(() => {
    const nextInfoList = []

    // METHOD
    if (hasText(messageType)) {
      const methodInfo: {
        key: ?string,
        title: string,
        description?: ?string,
      } = { title: 'Method:', key: messageType }

      switch (messageType) {
        case 'public_response':
          methodInfo.description = 'Public'
          break
        case 'tablet_response':
          methodInfo.description = 'Tablet'
          break
        case 'kiosk_response':
          methodInfo.description = 'Kiosk'
          break
        case 'internal':
          methodInfo.description = 'Internal'
          break
        case 'anonymous_public_response':
          methodInfo.description = 'Anonymous'
          break
        default:
          methodInfo.description =
            deliveryMethod === 'sms'
              ? formatPhoneNumberIntl(buildPhoneNumberValue(phone))
              : email
          break
      }
      nextInfoList.push(methodInfo)
    }

    // DATE INITIATED
    if (hasText(createdAt)) {
      const dateInitiatedInfo = {
        title: 'Date Initiated:',
        key: createdAt,
        description: (
          <Flex>
            {momentLocal(createdAt).format('LLL')}
            {!!remindersDate?.length && (
              <Tooltip
                label={
                  <div>
                    <Text $light $small className="text-white">
                      Reminders sent:
                    </Text>
                    <ul className="ml-4 list-disc text-white">
                      {remindersDate?.map((date, index) => (
                        <li key={index}>
                          <Text $light $small className="text-white">
                            {date}
                          </Text>
                        </li>
                      ))}
                    </ul>
                  </div>
                }
                mouseEnterDelay={0.5}
                placement="bottomLeft"
              >
                <div className="ml-2 flex h-5 w-5 cursor-pointer items-center justify-center rounded-full bg-yellow-300">
                  <BellOutlined />
                </div>
              </Tooltip>
            )}
          </Flex>
        ),
      }
      nextInfoList.push(dateInitiatedInfo)
    }

    // DATE COMPLETED
    if (hasText(dateCompleted)) {
      const completedInfo = {
        title: 'Date Completed:',
        key: dateCompleted,
        description: momentLocal(dateCompleted).format('LLL'),
      }
      nextInfoList.push(completedInfo)
    }

    // INTERNAL_USER
    if (internalUser) {
      nextInfoList.push({
        title: 'Internal User',
        key: internalUser,
        description: internalUser,
      })
    }

    // CLIENT ID
    if (clientId) {
      const clientInfo = {
        title: 'Client ID:',
        key: String(clientId),
        description: customer?.is_anonymous ? (
          <Text $muted>Anonymous</Text>
        ) : (
          <Link to={`/clients/${clientId || ''}`}>
            {`${firstName ?? ''} ${lastName ?? ''} <${clientId}>`}
          </Link>
        ),
      }
      nextInfoList.push(clientInfo)
    }

    if (teamDetails?.assigned_submission_owner && !isPartOfPacket) {
      const assignedUser = {
        title: 'Assigned User:',
        key: 'assignedUser',
        description: (
          <Select
            allowClear
            disabled={assignedLoading || isUsersLoading}
            filterOption={(input, option) =>
              option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            labelRender={(option) => (
              <Tooltip label={option.label} placement="bottomLeft">
                <Avatar
                  size="small"
                  style={{
                    backgroundColor: '#FA541C',
                    color: 'white',
                    marginBottom: 3,
                  }}
                >
                  {getUserLabel(option.value)}
                </Avatar>
              </Tooltip>
            )}
            loading={assignedLoading || isUsersLoading}
            mode={
              teamDetails?.config?.many_assigned_response
                ? 'multiple'
                : undefined
            }
            onChange={(values) => {
              setAssignedResponses(
                teamDetails?.config?.many_assigned_response
                  ? values
                  : values
                    ? [values]
                    : []
              )
            }}
            options={assignedUserOptions}
            size="large"
            style={{ width: '100%', marginTop: 5 }}
            value={assignedResponses?.map(
              (assignedResponse) => assignedResponse.assignee.id
            )}
            variant="filled"
          />
        ),
      }
      nextInfoList.push(assignedUser)
    }

    setInfoList(nextInfoList)
  }, [
    email,
    messageType,
    phone,
    deliveryMethod,
    createdAt,
    clientId,
    firstName,
    lastName,
    dateCompleted,
    customer,
    internalUser,
    remindersDate,
    isUsersLoading,
    assignedUserOptions,
    assignedResponses,
    getUserLabel,
    setAssignedResponses,
    teamDetails?.assigned_submission_owner,
    teamDetails?.config?.many_assigned_response,
    assignedLoading,
    isPartOfPacket,
  ])

  return (
    <div className="my-4 flex flex-col sm:mx-6 sm:flex-row sm:justify-between lg:mx-0 lg:flex-col">
      {map(infoList, ({ title, bodyClass, description, key }) => (
        <FormResponseInfo bodyClass={bodyClass} key={key} title={title}>
          {description}
        </FormResponseInfo>
      ))}
    </div>
  )
}

export default FormResponseInfoList
